import axios from 'axios';
import BaseApi from './base-api';
import numeral from 'numeral';

class BuildingsApi extends BaseApi {
  getBuildings(clientId, page) {
    let filter = '';
    if (clientId !== undefined) {
      filter = `by_client=${clientId}`;
    }

    filter += page ? `&page=${page}` : '';

    return axios
      .get(`${this.baseUrl}/buildings?${filter}`)
      .then((response) => {
        return {
          ok: true,
          buildings: response.data,
          total: numeral(response.headers['total']).value(),
          pageSize: numeral(response.headers['per-page']).value(),
        };
      })
      .catch((error) => {
        return {
          ok: false,
          errors: error.response.data.errors,
        };
      });
  }

  getBuilding(buildingId) {
    return axios
      .get(`${this.baseUrl}/buildings/${buildingId}`)
      .then((response) => {
        return {
          ok: true,
          building: response.data,
        };
      })
      .catch((error) => {
        return {
          ok: false,
          errors: error.response.data.errors,
        };
      });
  }

  getWorkOrders(buildingId, page, workOrderStatus, critical) {
    let filter = '';
    if (workOrderStatus !== undefined) {
      filter = `&${workOrderStatus.status}=true`;
    } else if (critical !== undefined && critical) {
      filter = '&critical=true';
    }

    return axios
      .get(
        `${this.baseUrl}/buildings/${buildingId}/work_orders?page=${
          page !== undefined ? page : 1
        }${filter}`
      )
      .then((response) => {
        return {
          ok: true,
          workOrders: {
            content: response.data,
            total: numeral(response.headers['total']).value(),
            totalComplete: numeral(response.headers['total-complete']).value(),
            totalCritical: numeral(response.headers['total-critical']).value(),
            totalDraft: numeral(response.headers['total-draft']).value(),
            totalInProgress: numeral(
              response.headers['total-in_progress']
            ).value(),
            totalPending: numeral(response.headers['total-pending']).value(),
            totalPlm: numeral(response.headers['total-plm']).value(),
            totalWoa: numeral(response.headers['total-woa']).value(),
            totalClosed: numeral(response.headers['total-closed']).value(),
            totalDeclined: numeral(response.headers['total-declined']).value(),
            pageSize: numeral(response.headers['per-page']).value(),
          },
        };
      })
      .catch((error) => {
        return {
          ok: false,
          errors: error.response.data.errors,
        };
      });
  }

  getProjects(buildingId, page, projectStatus) {
    return axios
      .get(
        `${this.baseUrl}/buildings/${buildingId}/projects?page=${
          page !== undefined ? page : 1
        }${projectStatus ? `&${projectStatus}=true` : ''}`
      )
      .then((response) => {
        return {
          ok: true,
          projects: {
            content: response.data,
            total: numeral(response.headers['total']).value(),
            totalPending: numeral(response.headers['total-pending']).value(),
            totalInProgress: numeral(
              response.headers['total-in_progress']
            ).value(),
            totalComplete: numeral(response.headers['total-complete']).value(),
            pageSize: numeral(response.headers['per-page']).value(),
          },
        };
      })
      .catch((error) => {
        return {
          ok: false,
          errors: error.response.data.errors,
        };
      });
  }

  createBuilding(data) {
    const formData = this.createFormData(data);
    return axios
      .post(this.baseUrl + '/buildings', formData)
      .then((response) => {
        return {
          ok: true,
          building: response.data,
        };
      })
      .catch((error) => {
        return {
          ok: false,
          errors: error.response.data.errors,
        };
      });
  }

  updateBuilding(buildingId, data) {
    const formData = this.createFormData(data);
    return axios
      .put(`${this.baseUrl}/buildings/${buildingId}`, formData)
      .then((response) => {
        return {
          ok: true,
          building: response.data,
        };
      })
      .catch((error) => {
        return {
          ok: false,
          errors: error.response.data.errors,
        };
      });
  }

  /**
   * Generates POST data to send to the API.
   *
   * @param data
   *    {
   *      name: string
   *      parkingOnSite: boolean (optional)
   *      location: {
   *        streetAddress: string
   *        unitNumber: string (optional)
   *        city: string
   *        state: string
   *        zipCode: string
   *      }
   *      manager: {  (optional)
   *        firstName: string
   *        lastName: string
   *        email: string
   *        phoneNumber: string (if defined, should not be empty)
   *      }
   *      engineer: {  (optional)
   *        firstName: string
   *        lastName: string
   *        email: string
   *        phoneNumber: string (if defined, should not be empty)
   *      }
   *      documentsToAdd: array (optional)
   *      documentsIdToRemove: array (optional)
   *      contractorsId: array (optional)
   *      unionsId: array (optional)
   *    }
   * @return {FormData}
   */
  createFormData(data) {
    let formData = new FormData();

    if (data.name !== undefined) {
      formData.append('building[name]', data.name);
    }

    if (data.propOwnerName !== undefined && data.propOwnerName !== null) {
      formData.append('building[property_owner_name]', data.propOwnerName);
    }

    if (data.parkingOnSite !== undefined && data.parkingOnSite !== null) {
      formData.append('building[parking_on_site]', data.parkingOnSite);
    }
    if (data.location.id) {
      formData.append('building[location_attributes][id]', data.location.id);
    }
    formData.append(
      'building[location_attributes][street_address_1]',
      data.location.streetAddress
    );
    if (data.location.unitNumber !== undefined) {
      formData.append(
        'building[location_attributes][street_address_2]',
        data.location.unitNumber
      );
    }
    formData.append('building[location_attributes][city]', data.location.city);
    formData.append(
      'building[location_attributes][state]',
      data.location.state
    );
    formData.append(
      'building[location_attributes][zip]',
      data.location.zipCode
    );

    if (data.propOwnerAddress) {
      if (data.propOwnerAddress.id) {
        formData.append(
          'building[property_owner_address_attributes][id]',
          data.propOwnerAddress.id
        );
      }
      formData.append(
        'building[property_owner_address_attributes][street_address_1]',
        data.propOwnerAddress.streetAddress
      );
      formData.append(
        'building[property_owner_address_attributes][street_address_2]',
        data.propOwnerAddress.unitNumber
      );
      formData.append(
        'building[property_owner_address_attributes][city]',
        data.propOwnerAddress.city
      );
      formData.append(
        'building[property_owner_address_attributes][state]',
        data.propOwnerAddress.state
      );
      formData.append(
        'building[property_owner_address_attributes][zip]',
        data.propOwnerAddress.zipCode
      );
    }

    if (data.manager !== undefined) {
      formData.append(
        'building[property_manager_attributes][first_name]',
        data.manager.firstName
      );
      formData.append(
        'building[property_manager_attributes][last_name]',
        data.manager.lastName
      );
      formData.append(
        'building[property_manager_attributes][email]',
        data.manager.email
      );
      if (data.manager.phoneNumber !== '') {
        // Don't send phone number if empty to avoid validation
        formData.append(
          'building[property_manager_attributes][phone_number]',
          data.manager.phoneNumber
        );
      }
    }

    if (data.engineer !== undefined) {
      formData.append(
        'building[engineer_attributes][first_name]',
        data.engineer.firstName
      );
      formData.append(
        'building[engineer_attributes][last_name]',
        data.engineer.lastName
      );
      formData.append(
        'building[engineer_attributes][email]',
        data.engineer.email
      );
      if (data.engineer.phoneNumber !== '') {
        // Don't send phone number if empty to avoid validation
        formData.append(
          'building[engineer_attributes][phone_number]',
          data.engineer.phoneNumber
        );
      }
    }

    // Append files added
    let documentsIndex = 0;
    if (data.documentsToAdd !== undefined && data.documentsToAdd.length > 0) {
      data.documentsToAdd.forEach((file) => {
        formData.append(
          'building[documents_attributes][' + documentsIndex + '][document]',
          file
        );
        documentsIndex++;
      });
    }

    if (
      data.documentsIdToRemove !== undefined &&
      data.documentsIdToRemove.length > 0
    ) {
      data.documentsIdToRemove.forEach((id) => {
        formData.append(
          'building[documents_attributes][' + documentsIndex + '][id]',
          id
        );
        formData.append(
          'building[documents_attributes][' + documentsIndex + '][_destroy]',
          'true'
        );
        documentsIndex++;
      });
    }

    if (data.contractorsId !== undefined) {
      if (data.contractorsId.length > 0) {
        data.contractorsId.forEach((subcontractorId) => {
          formData.append(
            'building[propietary_sub_contractor_ids][]',
            subcontractorId
          );
        });
      } else {
        formData.append('building[propietary_sub_contractor_ids][]', '');
      }
    }

    if (data.unionsId !== undefined) {
      data.unionsId.forEach((unionId) => {
        formData.append('building[union_ids][]', unionId);
      });
    }

    if (data.clientsId !== undefined && data.clientsId !== null) {
      if (data.clientsId.length > 0) {
        data.clientsId.forEach((clientId) => {
          formData.append('building[client_ids][]', clientId);
        });
      } else {
        formData.append('building[client_ids][]', '');
      }
    }

    return formData;
  }

  updateBuildingNameAndLocation(buildingId, locationId, data) {
    let putData = {
      building: {
        location_attributes: {
          id: locationId,
          street_address_1: data.streetAddress,
          street_address_2: data.unitNumber,
          city: data.city,
          state: data.state,
          zip: data.zipCode,
        },
      },
    };

    if (data.name !== '') {
      putData['building']['name'] = data.name;
    }

    return axios
      .put(this.baseUrl + '/buildings/' + buildingId, putData)
      .then((response) => {
        return {
          ok: true,
          newBuilding: response.data,
        };
      })
      .catch((error) => {
        return {
          ok: false,
          errors: error.response.data.errors,
        };
      });
  }

  uploadDocuments(buildingId, documents) {
    let data = new FormData();
    documents.forEach((document) => {
      data.append('building[documents_attributes][][document]', document);
    });

    return axios
      .put(this.baseUrl + '/buildings/' + buildingId, data)
      .then((response) => {
        return {
          ok: true,
          building: response.data,
        };
      })
      .catch((error) => {
        return {
          ok: false,
          errors: error.response.data.errors,
        };
      });
  }

  deleteDocument(buildingId, documentId) {
    return axios
      .put(this.baseUrl + '/buildings/' + buildingId, {
        building: {
          documents_attributes: [{ id: documentId, _destroy: true }],
        },
      })
      .then((response) => {
        return {
          ok: true,
          building: response.data,
        };
      })
      .catch((error) => {
        return {
          ok: false,
          errors: error.response.data.errors,
        };
      });
  }
}

export let buildingsApi = new BuildingsApi();
export default BuildingsApi = new BuildingsApi();



// WEBPACK FOOTER //
// ./src/services/api/buildings-api.js